/**
 * VehicleCategoriesPage - Cadastro de categorias de veículo (tipo "Outros")
 *
 * Página exclusiva do módulo Veículos (menu lateral "Veículos › Categorias").
 * Substitui o cadastro que antes vivia dentro da aba Veículos das Configurações.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Listagem alinhada ao padrão visual dos Templates de Checklist.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useMemo, useState } from 'react';
import { Info, MoreHorizontal, Pencil, Plus, Power, Tags, Trash2 } from 'lucide-react';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageFiltersBar,
  ListPageContent,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SettingsInfoBanner, StatusDot } from '@/components/tenant/SettingsInfoRow';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

/** Página de categorias de veículo */
export default function VehicleCategoriesPage() {
  const { categories, isLoading, upsertCategory, deleteCategory } = useVehiclesModule();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [editor, setEditor] = useState<{ open: boolean; id: string | null; name: string }>({ open: false, id: null, name: '' });

  /** Aplica os filtros localmente sobre a lista já devolvida pela RPC existente. */
  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pt-BR');
    return categories.filter((category) => {
      const matchesSearch = !normalizedSearch || category.name.toLocaleLowerCase('pt-BR').includes(normalizedSearch);
      const matchesStatus = status === 'all' || (status === 'active' ? category.is_active : !category.is_active);
      return matchesSearch && matchesStatus;
    });
  }, [categories, search, status]);

  /** Cria uma nova categoria a partir do input */
  const handleAdd = async () => {
    const name = editor.name.trim();
    if (!name) return;
    await upsertCategory.mutateAsync({ id: editor.id, name });
    setEditor({ open: false, id: null, name: '' });
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Categorias de Veículo"
        actions={(
          <Button size="xs" onClick={() => setEditor({ open: true, id: null, name: '' })}>
            <Plus className="mr-1.5 h-4 w-4" /> Nova categoria
          </Button>
        )}
      />

      <ListPageFiltersBar className="flex-nowrap">
        <Input
          className={`${LIST_FILTERS_INPUT_CLASS} min-w-0 flex-1 sm:max-w-sm`}
          value={search}
          maxLength={80}
          placeholder="Buscar categoria..."
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        <div className="w-full space-y-4">
          <SettingsInfoBanner icon={Info}>
            Carros, motos e caminhões usam a base oficial FIPE, compartilhada entre todas as
            empresas. As categorias abaixo são exclusivas da sua empresa e usadas quando o tipo do
            veículo é "Outros" (ex.: patinete, carretinha, jetski).
          </SettingsInfoBanner>

          {/* Listagem */}
          {isLoading ? (
            <Skeleton className="h-40 w-full rounded-lg" />
          ) : filteredCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <Tags className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nenhuma categoria encontrada.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Categoria</TableHead><TableHead className="w-28">Status</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
              <TableBody>
                {filteredCategories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={category.is_active ? 'emerald' : 'muted'} />
                        {category.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditor({ open: true, id: category.id, name: category.name })}>
                            <Pencil className="mr-2 h-4 w-4" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => upsertCategory.mutate({ id: category.id, name: category.name, isActive: !category.is_active })}>
                            <Power className="mr-2 h-4 w-4" /> {category.is_active ? 'Inativar' : 'Ativar'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" disabled={deleteCategory.isPending} onClick={() => deleteCategory.mutate(category.id)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </ListPageContent>

      {/* Cadastro e edição compartilham o mesmo formulário compacto. */}
      <Dialog open={editor.open} onOpenChange={(open) => setEditor((current) => ({ ...current, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editor.id ? 'Editar categoria' : 'Nova categoria'}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="vehicle-category-name">Nome</Label>
            <Input
              id="vehicle-category-name"
              autoFocus
              maxLength={80}
              value={editor.name}
              placeholder="Ex.: Patinete"
              onChange={(event) => setEditor((current) => ({ ...current, name: event.target.value }))}
              onKeyDown={(event) => event.key === 'Enter' && handleAdd()}
            />
          </div>
          <DialogFooter>
            <Button size="xs" variant="outline" onClick={() => setEditor({ open: false, id: null, name: '' })}>Cancelar</Button>
            <Button size="xs" disabled={!editor.name.trim() || upsertCategory.isPending} onClick={handleAdd}>
              {upsertCategory.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
